import { useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'

import http from '../../../../shared/http'
import { TApi, TRoutes } from '../../../../shared/const'
import { askConfirm } from '../../../../shared/confirmDialog'
import { formatVienna } from '../../../../shared/datetime'

import Btn from '../../../../UI/Btn'
import Loading from '../../../../UI/Loading'
import MiniHeader from '../../../../UI/MiniHeader'
import Table from '../../../../UI/Table'

import { STATUS_LABELS } from './labels'

import styles from './style.module.scss'

/**
 * Kursverwaltung: alle Kurse, die es gibt — veröffentlicht, im Entwurf oder
 * archiviert. Ein Klick auf die Zeile öffnet den Kurs zum Bearbeiten.
 *
 * Nur die Bundesverwaltung pflegt Kurse. Was sie hier ändert, sehen die
 * Schüler:innen sofort; deshalb entsteht ein neuer Kurs als Entwurf und wird
 * erst sichtbar, wenn ihn jemand ausdrücklich veröffentlicht.
 */
export default function AdminCourses() {
	const { t } = useTranslation()
	const history = useHistory()
	const { search } = useLocation()
	const [kurse, setKurse] = useState(null)

	useEffect(() => {
		http.get(TApi.ADMIN_COURSES)
			.then(({ data }) => setKurse(Array.isArray(data) ? data : data?.rows || []))
			.catch(() => setKurse([]))
	}, [])

	const anlegen = async () => {
		const titel = await askConfirm({
			title: t('New course'),
			text: t('The course is created as a draft. Students only see it once it is published.'),
			confirmLabel: t('Create'),
			prompt: { label: t('Title'), error: t('Please enter a title.') }
		})
		if (typeof titel !== 'string' || !titel.trim()) return
		http.post(TApi.ADMIN_COURSES, { title: titel.trim() }).then(({ data }) =>
			history.push(TRoutes.ADMIN_COURSE(data.id))
		)
	}

	/*
	 * „+ Kurs anlegen" in den Einstellungen führt mit `?neu=1` hierher und
	 * öffnet gleich das Fenster. Der Parameter verschwindet sofort wieder,
	 * sonst ginge es bei jedem Neuladen erneut auf.
	 */
	useEffect(() => {
		if (new URLSearchParams(search).get('neu') !== '1') return
		history.replace(TRoutes.ADMIN_COURSES)
		anlegen()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [search])

	const spalten = useMemo(
		() => [
			{ Header: t('Course'), accessor: 'title', width: 240 },
			{
				Header: t('Status'),
				accessor: 'status',
				width: 110,
				Cell: ({ value }) => (
					<span className={clsx(styles.status, styles[`status${value}`])}>
						{t(STATUS_LABELS[value] || value)}
					</span>
				)
			},
			{
				Header: t('For'),
				accessor: 'companyTypes',
				width: 120,
				Cell: ({ value }) => (value && value.length ? value.join(', ') : '—')
			},
			{ Header: t('Modules'), accessor: 'moduleCount', width: 80, align: 'center' },
			{ Header: t('Lessons'), accessor: 'lessonCount', width: 80, align: 'center' },
			{
				Header: t('Questions'),
				accessor: 'questionCount',
				width: 110,
				align: 'center',
				Cell: ({ row }) =>
					row.original.draftQuestionCount
						? `${row.original.questionCount} (${t('{{n}} not reviewed', {
								n: row.original.draftQuestionCount
						  })})`
						: row.original.questionCount
			},
			{ Header: t('Completions'), accessor: 'completionCount', width: 100, align: 'center' },
			{
				Header: t('Changed'),
				accessor: 'updatedAt',
				width: 100,
				Cell: ({ value }) => (value ? formatVienna(value, 'dd.MM.yyyy') : '—')
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	)

	return (
		<div className={styles.root}>
			<MiniHeader className={styles.miniHeader} title={t('Course management')} bg="#f8faff">
				<div className={styles.headActions}>
					<Btn theme="secondary" onClick={() => history.push(TRoutes.COURSE_REPORT)}>
						{t('Course evaluation')}
					</Btn>
					<Btn onClick={anlegen}>{t('New course')}</Btn>
				</div>
			</MiniHeader>

			<p className={styles.intro}>
				{t(
					'Courses with videos, quizzes and a certificate. Who sees a course is set per course (company types); whether courses are switched on at all and what a completion is worth on the invoice is set under Settings → Courses & credit.'
				)}
			</p>

			{!kurse && <Loading />}
			{kurse && (
				<div className="container">
					<Table
						columns={spalten}
						items={kurse}
						onRowClick={row => history.push(TRoutes.ADMIN_COURSE(row.id))}
					/>
				</div>
			)}
		</div>
	)
}
